/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import logger from '@/utils/logger';
import { type QueryClient, useQuery } from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';
import { type FormattedContentStackBlogMenuNavigation } from '@/features/contentstack/interface/models';
import { getContentStackBlogMenuNavigationContent } from '../../../features/contentstack/lib/blog/getContentStackBlogMenuNavigationContent';
import { type PageContextType } from '@/next-types';

const URL = `/api/contentstack/blog-menu-navigation`;

const getContentStackBlogMenuNavigationData = async (axiosInstance?: AxiosInstance) => {
  try {
    const { data } =
      await getAxios(axiosInstance).get<FormattedContentStackBlogMenuNavigation>(URL);
    return data;
  } catch (error) {
    logger.error({
      message: 'Error while fetching ContentStack blog menu navigation data',
      meta: {
        error,
      },
    });
    return null;
  }
};

const getBlogMenuNavigationKey = () => ['cms', 'blog-menu-navigation'] as const;

export const useContentStackBlogMenuNavigationData = () => {
  const queryResult = useQuery({
    queryKey: getBlogMenuNavigationKey(),
    queryFn: () => getContentStackBlogMenuNavigationData(),
    staleTime: Infinity,
  });

  const { data } = queryResult;

  return {
    data,
  };
};

export const prefetchContentStackBlogMenuNavigationData = ({
  queryClient,
  context,
}: {
  queryClient: QueryClient;
  context: PageContextType;
}) => {
  return queryClient.prefetchQuery({
    queryKey: getBlogMenuNavigationKey(),
    queryFn: () => getContentStackBlogMenuNavigationContent(context.req, context.query),
  });
};

export const getContentStackBlogMenuNavigationDataFromCache = (queryClient: QueryClient) => {
  return queryClient.getQueryData<FormattedContentStackBlogMenuNavigation>(
    getBlogMenuNavigationKey(),
  );
};
